import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import worldData from "../../data/countries-50m.json";
import { JOURNEY_MAPS } from "../../data/content";

const easeOut = [0.22, 1, 0.36, 1];
const W = 1000;
const H = 640;
const PAD = 72;

// Convert TopoJSON to GeoJSON features once at module load.
const COUNTRIES = feature(worldData, worldData.objects.countries).features;

export const findJourneyMap = (slug) => JOURNEY_MAPS.find((j) => j.slug === slug);

// Keep only polygons whose centroid falls inside an optional bounding box
// (drops distant territories, e.g. Spain's Canary Islands).
const clipGeometry = (feat, bbox) => {
  if (!bbox) return feat;
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const inBox = ([lng, lat]) =>
    lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat;
  const geom = feat.geometry;
  const polys = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
  const kept = polys.filter((poly) => {
    const ring = poly[0];
    let sx = 0;
    let sy = 0;
    ring.forEach((p) => {
      sx += p[0];
      sy += p[1];
    });
    return inBox([sx / ring.length, sy / ring.length]);
  });
  return {
    type: "Feature",
    properties: feat.properties,
    geometry: { type: "MultiPolygon", coordinates: kept.length ? kept : polys },
  };
};

// Faint atlas grid behind the landmass
const AtlasGrid = () => {
  const v = [0, 200, 400, 600, 800, 1000];
  const h = [0, 160, 320, 480, 640];
  return (
    <g stroke="#C6A87C" strokeWidth="0.5" opacity="0.10">
      {v.map((x) => (
        <line key={`v${x}`} x1={x} y1="0" x2={x} y2="640" />
      ))}
      {h.map((y) => (
        <line key={`h${y}`} x1="0" y1={y} x2="1000" y2={y} />
      ))}
    </g>
  );
};

const Pin = ({ x, y, city, isActive, testid, onSelect, labelDir = "top" }) => {
  const label =
    labelDir === "bottom"
      ? { x, y: y + 30, anchor: "middle" }
      : labelDir === "left"
      ? { x: x - 14, y: y + 5, anchor: "end" }
      : labelDir === "right"
      ? { x: x + 14, y: y + 5, anchor: "start" }
      : { x, y: y - 20, anchor: "middle" };
  return (
    <g
      data-testid={testid}
      onClick={onSelect}
      className="cursor-pointer focus:outline-none"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect()}
    >
      <circle cx={x} cy={y} r="26" fill="transparent" />
      {isActive && (
        <motion.circle
          cx={x}
          cy={y}
          fill="none"
          stroke="#C6A87C"
          strokeWidth="1.5"
          initial={{ r: 10, opacity: 0.6 }}
          animate={{ r: [10, 30], opacity: [0.6, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
        />
      )}
      <motion.circle
        cx={x}
        cy={y}
        animate={{ r: isActive ? 8 : 5 }}
        transition={{ duration: 0.4, ease: easeOut }}
        fill={isActive ? "#C6A87C" : "#0F1E3D"}
        stroke="#C6A87C"
        strokeWidth={isActive ? 0 : 1.5}
      />
      <text
        x={label.x}
        y={label.y}
        textAnchor={label.anchor}
        className="font-sans select-none"
        style={{ fontSize: "17px", letterSpacing: "0.02em", fontWeight: 600 }}
        fill={isActive ? "#F9F8F6" : "rgba(249,248,246,0.45)"}
      >
        {city}
      </text>
    </g>
  );
};

// Renders a single journey (leaf or grouped) — sub-tabs (if grouped),
// the map with real borders + points, the detail panel and stepper.
const JourneyMapCanvas = ({ primary, enquireName }) => {
  const [childIdx, setChildIdx] = useState(0);
  const [stopIdx, setStopIdx] = useState(0);

  const journey = primary.group ? primary.children[childIdx] : primary;
  const stop = journey.stops[stopIdx];

  const { outline, points } = useMemo(() => {
    const feat = COUNTRIES.find((c) => String(c.id) === String(journey.countryId));
    const geom = clipGeometry(feat, journey.bbox);
    const projection = geoMercator().fitExtent(
      [
        [PAD, PAD],
        [W - PAD, H - PAD],
      ],
      geom
    );
    const path = geoPath(projection);
    return {
      outline: path(geom),
      points: journey.stops.map((s) => projection(s.coords)),
    };
  }, [journey]);

  const selectChild = (i) => {
    setChildIdx(i);
    setStopIdx(0);
  };
  const goStop = (dir) => {
    const n = journey.stops.length;
    setStopIdx((prev) => (prev + dir + n) % n);
  };

  return (
    <div>
      {/* Country sub-tabs (for grouped journeys) */}
      {primary.group && (
        <div
          data-testid="journey-map-subtabs"
          className="flex flex-wrap items-center gap-x-6 gap-y-2 border-l-2 border-brass/40 pl-5"
        >
          {primary.children.map((c, i) => (
            <button
              key={c.slug}
              data-testid={`journey-map-subtab-${c.slug}`}
              onClick={() => selectChild(i)}
              className={`text-[0.7rem] tracking-[0.14em] uppercase font-semibold transition-colors duration-400 ${
                i === childIdx ? "text-brass" : "text-ivory/45 hover:text-ivory/80"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {/* Map panel */}
        <div className="relative border border-white/10 bg-ink/60 overflow-hidden aspect-[25/16]">
          <AnimatePresence mode="wait">
            <motion.div
              key={journey.slug}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full h-full"
            >
              <svg viewBox="0 0 1000 640" className="w-full h-full block">
                <AtlasGrid />
                <motion.path
                  d={outline}
                  fill="#132649"
                  stroke="#C6A87C"
                  strokeWidth="1.25"
                  strokeOpacity="0.55"
                  strokeLinejoin="round"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.9, ease: easeOut }}
                />
                {journey.stops.map((s, i) => (
                  <Pin
                    key={s.city}
                    x={points[i][0]}
                    y={points[i][1]}
                    city={s.city}
                    labelDir={s.labelDir}
                    isActive={i === stopIdx}
                    testid={`journey-map-pin-${journey.slug}-${i}`}
                    onSelect={() => setStopIdx(i)}
                  />
                ))}
              </svg>
            </motion.div>
          </AnimatePresence>

          <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
            <span className="eyebrow text-brass/70 text-[0.6rem]">{journey.region}</span>
            <span className="eyebrow text-ivory/30 text-[0.6rem]">
              {String(stopIdx + 1).padStart(2, "0")} /{" "}
              {String(journey.stops.length).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* Photo panel — matches the map's size */}
        <div className="relative border border-white/10 bg-ink/60 overflow-hidden aspect-[25/16]">
          <AnimatePresence mode="wait">
            <motion.img
              key={`${journey.slug}-${stopIdx}-img`}
              src={stop.image}
              alt={stop.city}
              data-testid="journey-map-stop-image"
              loading="lazy"
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: easeOut }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-ink/70 to-transparent pointer-events-none" />
        </div>
      </div>

      {/* Detail + controls below the panels */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${journey.slug}-${stopIdx}`}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5, ease: easeOut }}
              className="flex items-start gap-6"
            >
              <span className="font-serif text-brass/50 text-5xl md:text-6xl leading-none shrink-0">
                {String(stopIdx + 1).padStart(2, "0")}
              </span>
              <div>
                <p className="eyebrow text-brass">{stop.label}</p>
                <h3
                  data-testid="journey-map-stop-title"
                  className="font-serif text-3xl md:text-4xl text-ivory mt-2 tracking-[-0.01em]"
                >
                  {stop.city}
                </h3>
                <p className="mt-5 text-ivory/70 font-light leading-relaxed">{stop.body}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="lg:col-span-5 lg:pl-4">
          <div className="flex items-center gap-4">
            <button
              data-testid="journey-map-prev"
              onClick={() => goStop(-1)}
              aria-label="Previous stop"
              className="h-12 w-12 flex items-center justify-center border border-white/15 text-ivory/70 hover:border-brass hover:text-brass transition-colors duration-400"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              data-testid="journey-map-next"
              onClick={() => goStop(1)}
              aria-label="Next stop"
              className="h-12 w-12 flex items-center justify-center border border-white/15 text-ivory/70 hover:border-brass hover:text-brass transition-colors duration-400"
            >
              <ArrowRight size={18} />
            </button>
            <div className="ml-2 flex items-center gap-2 flex-wrap">
              {journey.stops.map((s, i) => (
                <button
                  key={s.city}
                  data-testid={`journey-map-tick-${journey.slug}-${i}`}
                  onClick={() => setStopIdx(i)}
                  aria-label={`Go to ${s.city}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === stopIdx ? "w-8 bg-brass" : "w-3 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
          </div>

          {enquireName && (
            <Link
              to={`/contact?journey=${encodeURIComponent(enquireName)}`}
              data-testid="journey-enquire-button"
              className="group mt-8 inline-flex items-center gap-3 bg-brass text-ink px-8 py-4 text-[0.74rem] tracking-[0.16em] uppercase font-semibold transition-all duration-500 hover:bg-[#d8bd93]"
            >
              Enquire about this journey
              <ArrowRight
                size={16}
                className="transition-transform duration-500 group-hover:translate-x-1"
              />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

// Full section with primary journey tabs — used on the Journeys index page.
export const JourneyMap = () => {
  const [primaryIdx, setPrimaryIdx] = useState(0);
  const primary = JOURNEY_MAPS[primaryIdx];

  return (
    <section data-testid="journey-map" className="border-y border-white/10 bg-[#0B1730]">
      <div className="yadid-container py-24 md:py-36">
        <div className="max-w-3xl">
          <p className="eyebrow text-brass mb-6">Explore the Routes</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ivory tracking-[-0.02em] leading-[1.05]">
            Trace the path, stop by stop.
          </h2>
          <p className="mt-8 text-lg text-ivory/60 font-light leading-relaxed">
            Each journey is a deliberate sequence of encounters. Explore the map and dwell
            at every destination.
          </p>
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          {JOURNEY_MAPS.map((j, i) => (
            <button
              key={j.slug}
              data-testid={`journey-map-tab-${j.slug}`}
              onClick={() => setPrimaryIdx(i)}
              className={`px-6 py-3 text-[0.72rem] tracking-[0.16em] uppercase font-semibold border transition-all duration-500 ${
                i === primaryIdx
                  ? "bg-brass text-ink border-brass"
                  : "text-ivory/70 border-white/15 hover:border-brass/60 hover:text-ivory"
              }`}
            >
              {j.name}
            </button>
          ))}
        </div>

        <div className="mt-6">
          <JourneyMapCanvas key={primary.slug} primary={primary} />
        </div>
      </div>
    </section>
  );
};

// Compact, single-journey map — used at the top of a Journey detail page.
export const JourneyRouteMap = ({ slug }) => {
  const primary = findJourneyMap(slug);
  if (!primary) return null;

  return (
    <section data-testid="journey-route-map" className="border-b border-white/10 bg-[#0B1730]">
      <div className="yadid-container py-20 md:py-28">
        <div className="max-w-2xl">
          <p className="eyebrow text-brass mb-6">The Destinations</p>
          <h2 className="font-serif text-4xl md:text-5xl text-ivory tracking-[-0.02em] leading-[1.05]">
            Where the journey goes.
          </h2>
        </div>
        <div className="mt-10">
          <JourneyMapCanvas primary={primary} enquireName={primary.name} />
        </div>
      </div>
    </section>
  );
};
